import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Home, Film, Tv, Search, Bookmark, LogOut, Clock, UserRound, CreditCard, Crown, Sparkles, Settings } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';

const Item = ({ to, icon: Icon, label, collapsed, accent }) => (
  <NavLink
    to={to}
    end={to === '/'}
    className={({ isActive }) =>
      `group flex items-center gap-4 px-4 py-3 rounded-xl tv-focusable ${
        isActive ? 'bg-white/10 border-white/20' : 'hover:bg-white/5'
      }`
    }
  >
    {({ isActive }) => (
      <>
        <span className={`flex items-center justify-center w-10 h-10 rounded-lg ${
          isActive ? (accent || 'bg-[#E50914]') + ' text-white' : 'bg-white/5 text-white/80'
        }`}>
          <Icon size={20} />
        </span>
        {!collapsed && (
          <span className={`text-[15px] font-medium ${isActive ? 'text-white' : 'text-white/75'}`}>{label}</span>
        )}
      </>
    )}
  </NavLink>
);

const Sidebar = ({ collapsed }) => {
  const { profile, setProfile } = useApp();
  const { user, logout } = useAuth();
  const { sub, isSubscribed } = useSubscription();
  const nav = useNavigate();
  const switchProfile = () => { setProfile(null); nav('/profiles'); };
  const signOut = async () => { await logout(); nav('/login'); };

  return (
    <aside
      className={`sidebar fixed left-0 top-0 bottom-0 z-40 bg-[#0a0a10]/90 backdrop-blur-xl border-r border-white/5 flex flex-col py-6 ${collapsed ? 'w-[88px]' : 'w-[260px]'}`}
    >
      <div className={`flex items-center gap-3 px-4 mb-10 ${collapsed ? 'justify-center' : ''}`}>
        <div className="w-10 h-10 rounded-xl bg-[#E50914] flex items-center justify-center shadow-lg shadow-[#E50914]/30">
          <span className="font-display text-2xl text-white leading-none">B</span>
        </div>
        {!collapsed && (
          <div className="leading-tight">
            <div className="font-display text-2xl tracking-wider text-white">BLUNT TV</div>
            <div className="text-[10px] uppercase text-white/50 tracking-[0.2em]">TV Edition</div>
          </div>
        )}
      </div>

      <nav className="flex-1 flex flex-col gap-2 px-3">
        <Item to="/" icon={Home} label="Home" collapsed={collapsed} />
        <Item to="/originals" icon={Sparkles} label="BLUNT Originals" collapsed={collapsed} />
        <Item to="/movies" icon={Film} label="Discover Movies" collapsed={collapsed} />
        <Item to="/tv" icon={Tv} label="Discover TV" collapsed={collapsed} />
        <Item to="/search" icon={Search} label="Search" collapsed={collapsed} />
        <Item to="/mylist" icon={Bookmark} label="My List" collapsed={collapsed} />
        {user?.is_admin && <Item to="/studio" icon={Crown} label="Studio" collapsed={collapsed} accent="bg-amber-500" />}
      </nav>

      <div className="px-3 mt-4 space-y-2">
        {!isSubscribed && (
          <button onClick={() => nav('/subscribe')} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-[#E50914]/15 border border-[#E50914]/30 text-white hover:bg-[#E50914]/25 tv-focusable">
            <CreditCard size={18} className="text-[#E50914]" />
            {!collapsed && (
              <div className="text-left leading-tight">
                <div className="text-sm font-semibold">Upgrade</div>
                <div className="text-[11px] text-white/60">Unlock playback</div>
              </div>
            )}
          </button>
        )}
        {isSubscribed && !collapsed && (
          <div className="px-4 py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs flex items-center gap-2">
            <Crown size={14} /> {sub?.plan?.toUpperCase()} active
          </div>
        )}
        <button
          onClick={() => nav('/account')}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-white/70 hover:bg-white/5 hover:text-white tv-focusable"
        >
          <Settings size={18} />
          {!collapsed && <span className="text-sm">Account & Billing</span>}
        </button>
        {profile && (
          <div className={`flex items-center gap-3 px-3 py-3 rounded-xl bg-white/5 ${collapsed ? 'justify-center' : ''}`}>
            <img src={profile.avatar} alt={profile.name} className="w-10 h-10 rounded-lg object-cover" style={{ boxShadow: `0 0 0 2px ${profile.color}` }} />
            {!collapsed && (
              <div className="leading-tight">
                <div className="text-sm font-semibold flex items-center gap-1">{profile.name}{profile.is_kids && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">KIDS</span>}</div>
                <div className="text-[11px] text-white/50 flex items-center gap-1"><Clock size={10} /> Tonight</div>
              </div>
            )}
          </div>
        )}
        <button
          onClick={switchProfile}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-white/70 hover:bg-white/5 hover:text-white tv-focusable"
        >
          <UserRound size={18} />
          {!collapsed && <span className="text-sm">Switch profile</span>}
        </button>
        <button
          onClick={signOut}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:bg-white/5 hover:text-white tv-focusable"
        >
          <LogOut size={18} />
          {!collapsed && (
            <div className="text-left leading-tight">
              <div className="text-sm">Sign out</div>
              {user?.email && <div className="text-[10px] text-white/40 truncate max-w-[150px]">{user.email}</div>}
            </div>
          )}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
